import React, { useState } from "react";
import { PenTool, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import MegaLoader from "../../components/MegaLoader";
import { usePerformRequest } from "../../lib/usePerformRequest";
import { Endpoints } from "../../lib/endpoints";
import { NonPaginatedResponse } from "../../lib/responses";
import CreateSignatureModal from "./CreateSignatureModal";
import ValidateSignatureModal from "./ValidateSignatureModal";

export interface ISignatureProps {
  id: string;
  issuedTo: string;
  description?: string;
  createdAt: number;
}

const SignaturesPage: React.FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isValidateModalOpen, setIsValidateModalOpen] = useState(false);

  const {
    data: responseData,
    isLoading,
    reloadData,
  } = usePerformRequest<NonPaginatedResponse<ISignatureProps[]>>({
    method: Endpoints.GetSignatures().method,
    route: Endpoints.GetSignatures().route,
  });

  const signatures = responseData?.data || [];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-900 to-black pt-12 pb-8 px-6 lg:px-12 border-b border-gray-800">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>
        <div className="relative z-10 max-w-4xl mx-auto text-center space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center justify-center p-3 bg-purple-500/10 rounded-2xl mb-4"
          >
            <PenTool className="w-8 h-8 text-purple-400" />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-gray-400"
          >
            Signatures Authentication
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg max-w-xl mx-auto"
          >
            Generate and manage your authentic 8-digit alphanumeric e-signature
            codes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="pt-6 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600 text-white rounded-xl font-medium shadow-lg hover:shadow-purple-500/25 transition-all duration-200"
            >
              <Plus className="w-5 h-5 mr-2" />
              Generate New Signature
            </button>
            <button
              onClick={() => setIsValidateModalOpen(true)}
              className="inline-flex items-center px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl font-medium shadow-lg transition-all duration-200 border border-gray-700"
            >
              Validate Signature
            </button>
          </motion.div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {isLoading ? (
          <MegaLoader />
        ) : signatures.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <PenTool className="w-16 h-16 mx-auto mb-4 text-gray-700" />
            <p className="text-xl font-medium">No signatures found</p>
            <p className="text-sm">
              Generate your first signature code to get started.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <AnimatePresence>
              {signatures.map((sig: ISignatureProps, index: number) => (
                <motion.div
                  key={sig.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden hover:border-gray-700 hover:shadow-2xl transition-all duration-300"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-blue-500 opacity-50 group-hover:opacity-100 transition-opacity" />

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono font-medium bg-gray-800 text-gray-400 border border-gray-700">
                        {new Date(sig.createdAt).toLocaleDateString()}
                      </span>
                    </div>

                    <div>
                      <h3 className="text-2xl font-bold font-mono text-purple-400 mb-2 transition-colors">
                        {sig.id}
                      </h3>
                      <p className="text-sm text-gray-400 mb-4 flex items-start flex-col">
                        Issued To:
                        <span className="text-gray-200 font-medium bg-gray-800 px-2 py-1 mt-1 rounded inline-block">
                          {sig.issuedTo}
                        </span>
                      </p>
                      <p className="text-gray-500 text-sm line-clamp-3 leading-relaxed min-h-[40px] mb-4">
                        {sig.description || "No description provided."}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      <CreateSignatureModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          reloadData();
        }}
      />

      <ValidateSignatureModal
        isOpen={isValidateModalOpen}
        onClose={() => setIsValidateModalOpen(false)}
      />
    </div>
  );
};

export default SignaturesPage;
